export interface Location {
  id: string;
  name: string;
  description?: string;
  address: string;
  city: string;
  country: string;
  capacity?: number;
  amenities?: string[];
  imageUrl?: string;
  latitude?: number;
  longitude?: number;
}

export interface EventLocationRelation {
  id: string;
  eventId: string;
  locationId: string;
  isPrimary: boolean;
  Location: Location;
}

export interface Event {
  id: string;
  title: string;
  description: string;
  startTime: Date;
  endTime: Date;
  location: string; // Legacy field for backward compatibility
  locations?: Location[]; // New locations array from EventLocations relation
  EventLocations?: EventLocationRelation[]; // Full relation data from backend
  category: EventCategory;
  status: EventStatus;
  capacity?: number;
  currentAttendees?: number;
  imageUrl?: string;
  speakers?: Speaker[];
  tags?: string[];
  isReminder?: boolean;
  isStreaming?: boolean;
  streamUrl?: string;
  translations?: Record<string, EventTranslation>;
}

export interface EventTranslation {
  title: string;
  description: string;
}

export interface Speaker {
  id: string;
  name: string;
  title: string;
  bio: string;
  avatarUrl?: string;
  company?: string;
}

// Dynamic category type - categories are fetched from backend
export type EventCategory = string;

// Legacy enum for backward compatibility with old data
export enum LegacyEventCategory {
  KARAOKE = 'KARAOKE',
  GAMING = 'GAMING',
  WORKSHOP = 'WORKSHOP',
  EXHIBITION = 'EXHIBITION',
  CONFERENCE = 'CONFERENCE',
  NETWORKING = 'NETWORKING',
  SOCIAL = 'SOCIAL',
  OTHER = 'OTHER',
}

export enum EventStatus {
  UPCOMING = 'UPCOMING',
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  CANCELLED = 'CANCELLED',
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  preferences: UserPreferences;
}

export interface UserPreferences {
  language: string;
  theme: ThemeType;
  notifications: NotificationSettings;
  categories: string[];
}

export interface NotificationSettings {
  push: boolean;
  email: boolean;
  reminderMinutes: number;
}

export enum ThemeType {
  MANGA = 'manga',
  CORPORATE = 'corporate',
  FESTIVAL = 'festival',
  ACADEMIC = 'academic',
}

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'acty';
  timestamp: Date;
  isTyping?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface WebSocketMessage {
  type: 'event_update' | 'event_created' | 'event_deleted' | 'reminder' | 'chat';
  payload: any;
  timestamp: Date;
}

// UI State Types
export interface AppState {
  isLoading: boolean;
  isOffline: boolean;
  selectedDate: Date;
  currentEvent: Event | null;
  isEventModalOpen: boolean;
  isChatOpen: boolean;
  user: User | null;
  theme: ThemeType;
}

// Form Types
export interface ReminderForm {
  eventId: string;
  minutesBefore: number;
  enabled: boolean;
}

export interface FeedbackForm {
  eventId: string;
  rating: number;
  comment: string;
  anonymous: boolean;
}

// Error Types
export interface AppError {
  code: string;
  message: string;
  details?: any;
}

// PWA Types
export interface PWAInstallPrompt {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Analytics Types
export interface EventInteraction {
  eventId: string;
  action: 'view' | 'remind' | 'share' | 'favorite';
  timestamp: Date;
  duration?: number;
}

// Utility Types
export type EventCardSize = 'compact' | 'default' | 'expanded';
export type LoadingState = 'idle' | 'loading' | 'success' | 'error';
export type ConnectionStatus = 'online' | 'offline' | 'reconnecting';